/**
 * VendingCommissionsPage — v2.5.912
 *
 * Full frontend for the vending commission payout ledger. Finance teams can:
 *   • Trigger the monthly compute for any YYYY-MM (or leave the nightly cron alone)
 *   • Filter by month + client + status
 *   • Inspect the per-tier breakdown of any single payout
 *   • Mark an entry paid with cheque reference
 *   • Export the whole batch as CSV for AP import
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Coins, RefreshCw, PlayCircle, Download, CheckCircle2, ChevronRight,
  Loader2, ArrowLeft, Calendar as CalendarIcon,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/vending/commissions`;
const now = new Date();
const currentYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  paid: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  disputed: 'bg-rose-100 text-rose-800 border-rose-200',
};

export default function VendingCommissionsPage() {
  const navigate = useNavigate();
  const [ledger, setLedger] = useState([]);
  const [summary, setSummary] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [computing, setComputing] = useState(false);
  const [filters, setFilters] = useState({ year_month: '', client_id: '', status: '' });
  const [computeMonth, setComputeMonth] = useState(currentYm);
  const [detail, setDetail] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(null); // ledger row for pay dialog

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.year_month) params.set('year_month', filters.year_month);
      if (filters.client_id) params.set('client_id', filters.client_id);
      if (filters.status) params.set('status', filters.status);
      const [l, s, c] = await Promise.all([
        axios.get(`${API}/ledger${params.toString() ? '?' + params.toString() : ''}`),
        axios.get(`${API}/summary`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vending/clients`),
      ]);
      setLedger(l.data || []);
      setSummary(s.data || []);
      setClients(c.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load ledger');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const compute = async () => {
    const [yStr, mStr] = computeMonth.split('-');
    setComputing(true);
    try {
      const { data } = await axios.post(`${API}/compute-month`, {
        year: parseInt(yStr, 10), month: parseInt(mStr, 10), dry_run: false,
      });
      toast.success(`Computed ${computeMonth} — ${data.entries_written + data.entries_updated} entries · total ${data.total_payout.toLocaleString()}`);
      setFilters(f => ({ ...f, year_month: computeMonth }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Compute failed');
    } finally { setComputing(false); }
  };

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (filters.year_month) params.set('year_month', filters.year_month);
    if (filters.status) params.set('status', filters.status);
    const url = `${API}/ledger.csv${params.toString() ? '?' + params.toString() : ''}`;
    // Fetch + blob to keep bearer auth
    axios.get(url, { responseType: 'blob' }).then(res => {
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `vending-commissions-${filters.year_month || 'all'}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success('CSV downloaded');
    }).catch(e => toast.error(e.response?.data?.detail || 'CSV export failed'));
  };

  const totals = ledger.reduce((acc, r) => {
    acc.count += 1;
    acc.gross += Number(r.monthly_gross_revenue || 0);
    acc.payout += Number(r.computed_payout || 0);
    if (r.status === 'paid') acc.paid += Number(r.computed_payout || 0);
    if (r.status === 'pending') acc.pending += Number(r.computed_payout || 0);
    return acc;
  }, { count: 0, gross: 0, payout: 0, paid: 0, pending: 0 });

  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6" data-testid="vending-commissions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <button onClick={() => navigate('/vending')} className="text-xs text-gray-500 hover:text-gray-700 mb-1 flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Back to Vending Fleet
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <Coins className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600 flex-shrink-0" />
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold" data-testid="commissions-title">Commission Ledger</h1>
            <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px] sm:text-xs">Payouts</Badge>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Monthly host commission payouts computed from your contract rental terms.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={load} disabled={loading} className="h-9" data-testid="commissions-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={exportCsv} className="h-9" data-testid="commissions-export-csv">
            <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Compute Month Card */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1 min-w-0">
              <Label className="text-[11px] uppercase tracking-wide text-gray-600 font-medium">Compute payouts for</Label>
              <Input
                type="month"
                value={computeMonth}
                onChange={e => setComputeMonth(e.target.value)}
                className="mt-1 h-9 text-sm max-w-xs"
                data-testid="commissions-compute-month"
              />
              <div className="text-[11px] text-gray-500 mt-1">Aggregates monthly gross for every active contract and applies its rental terms stack.</div>
            </div>
            <Button
              onClick={compute}
              disabled={computing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-sm"
              data-testid="commissions-compute-btn"
            >
              {computing ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Computing…</> : <><PlayCircle className="h-3.5 w-3.5 mr-1" /> Run compute</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
        <KpiCard label="Entries" value={totals.count} />
        <KpiCard label="Monthly gross" value={totals.gross.toLocaleString(undefined, { maximumFractionDigits: 0 })} />
        <KpiCard label="Pending payout" value={totals.pending.toLocaleString(undefined, { maximumFractionDigits: 0 })} tone="amber" />
        <KpiCard label="Paid this batch" value={totals.paid.toLocaleString(undefined, { maximumFractionDigits: 0 })} tone="emerald" />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-gray-600 font-medium">Month</Label>
              <Input type="month" value={filters.year_month} onChange={e => setFilters(f => ({ ...f, year_month: e.target.value }))} className="mt-1 h-9 text-sm" data-testid="commissions-filter-month" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-gray-600 font-medium">Client</Label>
              <Select value={filters.client_id || 'all'} onValueChange={v => setFilters(f => ({ ...f, client_id: v === 'all' ? '' : v }))}>
                <SelectTrigger className="mt-1 h-9 text-sm" data-testid="commissions-filter-client"><SelectValue placeholder="All clients" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All clients</SelectItem>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-gray-600 font-medium">Status</Label>
              <Select value={filters.status || 'all'} onValueChange={v => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
                <SelectTrigger className="mt-1 h-9 text-sm" data-testid="commissions-filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="disputed">Disputed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ledger List */}
      {loading && <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading ledger…</div>}
      {!loading && ledger.length === 0 && (
        <Card><CardContent className="p-6 text-center text-sm text-gray-500">
          No ledger entries yet for the selected filters. Run <b>Compute</b> above to generate the current month.
        </CardContent></Card>
      )}
      <div className="space-y-2">
        {ledger.map(row => {
          const client = clients.find(c => c.id === row.client_id);
          return (
            <Card key={row.id || `${row.contract_id}-${row.year_month}`} data-testid={`ledger-row-${row.id}`} onClick={() => setDetail(row)} className="cursor-pointer hover:border-emerald-300 transition-colors">
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2 flex-wrap">
                      <div className="font-semibold text-sm sm:text-base break-words min-w-0">{client?.name || row.client_id?.slice(0, 8) || 'Unknown client'}</div>
                      <Badge variant="outline" className={`text-[10px] sm:text-xs ${STATUS_STYLES[row.status] || ''}`}>{row.status}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      <Badge variant="outline" className="text-[10px] sm:text-xs">
                        <CalendarIcon className="h-3 w-3 mr-1" /> {row.year_month}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] sm:text-xs font-mono">{row.contract_number || row.contract_id?.slice(0, 8)}</Badge>
                      <Badge variant="outline" className="text-[10px] sm:text-xs">{row.machine_count} machines</Badge>
                      <Badge variant="outline" className="text-[10px] sm:text-xs">{row.payout_method}</Badge>
                    </div>
                    <div className="text-[11px] sm:text-xs text-gray-500 mt-2 flex flex-wrap gap-3">
                      <span>Gross: <b>{row.payout_currency} {Number(row.monthly_gross_revenue).toLocaleString(undefined, { maximumFractionDigits: 2 })}</b></span>
                      <span>Vends: {row.monthly_vend_count}</span>
                      <span>Payout: <b className="text-emerald-700">{row.payout_currency} {Number(row.computed_payout).toLocaleString(undefined, { maximumFractionDigits: 2 })}</b></span>
                    </div>
                    {row.cheque_reference && (
                      <div className="text-[11px] text-gray-400 mt-1">Cheque ref: {row.cheque_reference}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {row.status !== 'paid' && (
                      <Button
                        size="sm"
                        onClick={e => { e.stopPropagation(); setMarkingPaid(row); }}
                        className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs"
                        data-testid={`mark-paid-${row.id}`}
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark paid
                      </Button>
                    )}
                    <ChevronRight className="h-4 w-4 text-gray-300 hidden sm:block" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Summary panel */}
      {summary.length > 0 && (
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="text-sm font-semibold mb-2">Per-client rollup (all time)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {summary.map(s => (
                <div key={s.client_id} className="p-2 border rounded text-xs">
                  <div className="font-medium truncate">{s.client_name}</div>
                  <div className="text-gray-500 flex gap-3 mt-1">
                    <span>Pending: <b className="text-amber-700">{(s.pending || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</b></span>
                    <span>Paid: <b className="text-emerald-700">{(s.paid || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</b></span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={o => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="commissions-detail-dialog">
          <DialogHeader><DialogTitle>Payout breakdown — {detail?.year_month}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <Field label="Contract" value={detail.contract_number || detail.contract_id} />
                <Field label="Machines" value={detail.machine_count} />
                <Field label="Monthly gross" value={`${detail.payout_currency} ${Number(detail.monthly_gross_revenue).toLocaleString()}`} />
                <Field label="Total vends" value={detail.monthly_vend_count} />
                <Field label="Computed payout" value={`${detail.payout_currency} ${Number(detail.computed_payout).toLocaleString()}`} highlight />
                <Field label="Method" value={detail.payout_method} />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-600 font-medium mb-1">Tier-by-tier breakdown</div>
                <div className="space-y-1">
                  {(detail.breakdown || []).map((b, i) => (
                    <div key={i} className="p-2 border rounded text-xs bg-gray-50">
                      <div className="flex justify-between">
                        <span className="font-semibold uppercase text-[10px] text-gray-600">{b.kind.replace(/_/g, ' ')}</span>
                        <span className={b.amount >= 0 ? 'text-emerald-700 font-mono' : 'text-rose-700 font-mono'}>
                          {detail.payout_currency} {Number(b.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="text-gray-600 mt-1">{b.note}</div>
                    </div>
                  ))}
                </div>
              </div>
              {detail.status === 'paid' && (
                <div className="p-2 border border-emerald-200 bg-emerald-50 rounded text-xs">
                  Paid on <b>{detail.paid_at}</b> by <b>{detail.paid_by}</b>. Cheque ref: <b>{detail.cheque_reference || '—'}</b>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark-paid dialog */}
      <MarkPaidDialog row={markingPaid} onClose={() => setMarkingPaid(null)} onDone={() => { setMarkingPaid(null); load(); }} />
    </div>
  );
}

function KpiCard({ label, value, tone }) {
  const cls = tone === 'emerald' ? 'text-emerald-700' : tone === 'amber' ? 'text-amber-700' : '';
  return (
    <Card><CardContent className="p-3 sm:p-4">
      <div className="text-[10px] sm:text-xs text-gray-500 uppercase truncate">{label}</div>
      <div className={`text-lg sm:text-2xl font-bold mt-0.5 ${cls}`}>{value}</div>
    </CardContent></Card>
  );
}

function Field({ label, value, highlight }) {
  return (
    <div className="p-2 border rounded bg-gray-50">
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-sm ${highlight ? 'font-bold text-emerald-700' : 'font-medium'} truncate`}>{value}</div>
    </div>
  );
}

function MarkPaidDialog({ row, onClose, onDone }) {
  const [cheque, setCheque] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setCheque(''); setNotes(''); }, [row]);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/${row.id}/mark-paid`, { cheque_reference: cheque, notes });
      toast.success(`Marked ${row.year_month} as paid`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Mark-paid failed');
    } finally { setBusy(false); }
  };

  if (!row) return null;
  return (
    <Dialog open={!!row} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="mark-paid-dialog">
        <DialogHeader><DialogTitle>Mark {row.year_month} as paid</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-gray-600">Payout: <b>{row.payout_currency} {Number(row.computed_payout).toLocaleString()}</b> via {row.payout_method}</div>
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-gray-600">Cheque / transfer ref</Label>
            <Input value={cheque} onChange={e => setCheque(e.target.value)} placeholder="CHK-000123" className="mt-1 h-9 text-sm" data-testid="mark-paid-cheque-ref" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-gray-600">Notes (optional)</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="mt-1 text-sm" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy} className="h-9 text-xs">Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 h-9 text-xs" data-testid="mark-paid-submit">
              {busy ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Saving…</> : 'Confirm paid'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
